import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import { db } from '../../services/firebase';
import { collection, getDocs } from 'firebase/firestore';

const UserManagement = () => {
  const { isDemo } = useAuth();
  const [users, setUsers] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [form, setForm] = useState({
    email: '',
    password: '',
    nombre: '',
    rol: 'empleado',
    departamento: '',
    sucursalId: ''
  });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    nombre: '',
    rol: 'empleado',
    departamento: '',
    sucursalId: '',
    activo: true
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUsers();
    loadSucursales();
  }, [isDemo]);

  const loadUsers = async () => {
    if (isDemo) {
      setUsers([
        {
          id: 'demo-emp-1',
          nombre: 'Usuario Demo',
          email: 'demo@comarsa.com',
          rol: 'empleado',
          departamento: 'Demostración',
          sucursalId: 'demo-suc',
          activo: true
        }
      ]);
      return;
    }
    try {
      const list = await adminService.getUsers();
      setUsers(list);
    } catch (error) {
      setMessage('Error al cargar usuarios');
    }
  };

  const loadSucursales = async () => {
    if (isDemo) {
      setSucursales([{ id: 'demo-suc', nombre: 'Hidalgo (Demo)' }]);
      return;
    }
    try {
      const snapshot = await getDocs(collection(db, 'sucursales'));
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSucursales(items);
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (isDemo) {
      setMessage('ℹ️ En modo demostración la creación de usuarios está desactivada.');
      return;
    }
    setLoading(true);
    setMessage('');
    const result = await adminService.createUser({
      email: form.email,
      password: form.password,
      nombre: form.nombre,
      rol: form.rol,
      departamento: form.departamento,
      sucursalId: form.sucursalId
    });
    if (result.success) {
      setMessage('✅ Usuario creado exitosamente');
      setForm({ email: '', password: '', nombre: '', rol: 'empleado', departamento: '', sucursalId: '' });
      loadUsers();
    } else {
      setMessage(`❌ Error: ${result.error}`);
    }
    setLoading(false);
  };

  const handleEditClick = (user) => {
    if (isDemo) {
      setMessage('ℹ️ En modo demostración la edición está desactivada.');
      return;
    }
    setEditId(user.id);
    setEditForm({
      nombre: user.nombre || '',
      rol: user.rol || 'empleado',
      departamento: user.departamento || '',
      sucursalId: user.sucursalId || '',
      activo: user.activo !== false
    });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (isDemo) {
      setMessage('ℹ️ En modo demostración no se guardan cambios.');
      return;
    }
    setLoading(true);
    setMessage('');
    const result = await adminService.updateUser(editId, editForm);
    if (result.success) {
      setMessage('✅ Usuario actualizado');
      setEditId(null);
      loadUsers();
    } else {
      setMessage(`❌ Error: ${result.error}`);
    }
    setLoading(false);
  };

  const handleDeleteUser = async (userId) => {
    if (isDemo) {
      setMessage('ℹ️ En modo demostración no se eliminan usuarios.');
      return;
    }
    if (window.confirm('¿Eliminar este usuario?')) {
      const result = await adminService.deleteUser(userId);
      if (result.success) {
        setMessage('✅ Usuario eliminado');
        loadUsers();
      } else {
        setMessage(`❌ Error: ${result.error}`);
      }
    }
  };

  return (
    <div className="user-management">
      <h3>Gestión de Usuarios</h3>

      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.85rem', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <i className="fa-solid fa-shield-halved" style={{ fontSize: '1.1rem' }}></i>
        <span><strong>Control de Acceso Estricto:</strong> Solo los correos registrados aquí podrán iniciar sesión (con contraseña o mediante <strong>Cuenta Google</strong>). Los correos no registrados serán bloqueados automáticamente.</span>
      </div>

      {/* Formulario de creación */}
      <form onSubmit={handleCreateUser} className="form-section">
        <h4>Crear nuevo usuario</h4>
        <div className="field">
          <label>Nombre completo</label>
          <input type="text" value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} required />
        </div>
        <div className="field">
          <label>Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
        </div>
        <div className="field">
          <label>Contraseña</label>
          <input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength="6" />
        </div>
        <div className="field">
          <label>Departamento</label>
          <input type="text" value={form.departamento} onChange={(e) => setForm({...form, departamento: e.target.value})} />
        </div>
        <div className="field">
          <label>Rol</label>
          <select value={form.rol} onChange={(e) => setForm({...form, rol: e.target.value})}>
            <option value="empleado">Empleado</option>
            <option value="supervisor">Supervisor</option>
            <option value="admin">Administrador</option>
          </select>
        </div>
        <div className="field">
          <label>Sucursal</label>
          <select value={form.sucursalId} onChange={(e) => setForm({...form, sucursalId: e.target.value})}>
            <option value="">Sin asignar</option>
            {sucursales.map(suc => (
              <option key={suc.id} value={suc.id}>{suc.nombre}</option>
            ))}
          </select>
        </div>
        <button type="submit" disabled={loading} className="btn btn-primary">
          {loading ? 'Creando...' : 'Crear Usuario'}
        </button>
      </form>

      {message && <div className="message">{message}</div>}

      {/* Formulario de edición */}
      {editId && (
        <form onSubmit={handleUpdateUser} className="form-section" style={{ marginTop: '20px' }}>
          <h4>Editar usuario</h4>
          <div className="field">
            <label>Nombre</label>
            <input type="text" value={editForm.nombre} onChange={(e) => setEditForm({...editForm, nombre: e.target.value})} required />
          </div>
          <div className="field">
            <label>Rol</label>
            <select value={editForm.rol} onChange={(e) => setEditForm({...editForm, rol: e.target.value})}>
              <option value="empleado">Empleado</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <div className="field">
            <label>Departamento</label>
            <input type="text" value={editForm.departamento} onChange={(e) => setEditForm({...editForm, departamento: e.target.value})} />
          </div>
          <div className="field">
            <label>Sucursal</label>
            <select value={editForm.sucursalId} onChange={(e) => setEditForm({...editForm, sucursalId: e.target.value})}>
              <option value="">Sin asignar</option>
              {sucursales.map(suc => (
                <option key={suc.id} value={suc.id}>{suc.nombre}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Estado</label>
            <select value={editForm.activo} onChange={(e) => setEditForm({...editForm, activo: e.target.value === 'true'})}>
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </div>
          <button type="submit" disabled={loading} className="btn btn-success">
            {loading ? 'Actualizando...' : 'Guardar Cambios'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setEditId(null)}>
            Cancelar
          </button>
        </form>
      )}

      {/* Tabla de usuarios */}
      <div className="table-wrapper">
        <table className="erp-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Sucursal</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => {
              const sucursal = sucursales.find(s => s.id === user.sucursalId);
              return (
                <tr key={user.id}>
                  <td>{user.nombre}</td>
                  <td>{user.email}</td>
                  <td>{user.rol}</td>
                  <td>{sucursal ? sucursal.nombre : '—'}</td>
                  <td>
                    <span className={`badge ${user.activo !== false ? 'badge-success' : 'badge-danger'}`}>
                      {user.activo !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-primary btn-sm" onClick={() => handleEditClick(user)}>
                      <i className="fa-solid fa-pen"></i>
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(user.id)}>
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;